import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from 'recharts';
import styles from '../../../assets/styles/LayoutShared.module.css';

// ─── Paletas ──────────────────────────────────────────────────────────────────

const PALETTE      = ['#00b37e','#3498db','#f39c12','#e74c3c','#9b59b6','#1abc9c','#e67e22','#2ecc71','#e91e63','#607d8b'];
const PALETTE_WARM = ['#e74c3c','#e67e22','#f39c12','#f1c40f','#ff7675','#fd79a8'];
const PALETTE_COOL = ['#00b37e','#3498db','#9b59b6','#1abc9c','#2980b9','#8e44ad'];

const LABEL_CAT: Record<string, string> = {
  operacao_agricola: 'Op. Agrícola', custo: 'Custo', receita: 'Receita',
  manutencao: 'Manutenção', abastecimento: 'Abastecimento', outros: 'Outros',
};
const STATUS_LABELS: Record<string, string> = {
  agendado: 'Agendado', registrado: 'Registrado', em_andamento: 'Em andamento',
  concluido: 'Concluído', cancelado: 'Cancelado',
};
const STATUS_COLORS: Record<string, string> = {
  agendado: '#9b59b6', registrado: '#3498db', em_andamento: '#f39c12',
  concluido: '#00b37e', cancelado: '#aaa',
};
const CATEGORIAS_DESPESA = ['custo','manutencao','abastecimento'];
const CATEGORIAS_RECEITA = ['receita'];

const CORES_TIPO_OP: Record<string, string> = {
  'Colheita': '#f39c12', 'Plantio': '#00b37e', 'Pulverização': '#3498db',
  'Adubação': '#9b59b6', 'Irrigação': '#1abc9c', 'Capina/Roçagem': '#e67e22',
  'Poda': '#8e44ad', 'Aplicação': '#2980b9', 'Transporte': '#607d8b', 'Outros': '#aaa',
};

const formatBRL  = (v: number) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatMes  = (mes: string) => {
  const [ano, m] = mes.split('-');
  const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  return `${meses[parseInt(m)-1]}/${ano.slice(2)}`;
};
const parseValor = (ev: any) => parseFloat(ev.valor_total || ev.valor || '0') || 0;

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface DashData {
  resumo:            { totalEventos: number; totalReceitas: number; totalDespesas: number; saldo: number };
  porCategoria:      { categoria: string; count: number; total: number }[];
  porArea:           { area: string; count: number }[];
  porCultura:        { cultura: string; count: number }[];
  porVeiculo:        { veiculo: string; count: number }[];
  porOrigem:         { origem: string; count: number }[];
  porStatus:         { status: string; count: number }[];
  evolucaoMensal:    { mes: string; despesas: number; receitas: number; count: number }[];
  porCategoriaCusto: { categoria_custo: string; total: number }[];
  porTipoOperacao:   { tipo: string; count: number }[];
  agendados:         any[]; // eventos com status=agendado para o calendário
}

// ─── Calcula tudo em memória ──────────────────────────────────────────────────

function calcularDash(eventos: any[]): DashData {
  let totalDespesas = 0, totalReceitas = 0;
  const catMap:     Record<string, { count: number; total: number }> = {};
  const areaMap:    Record<string, number> = {};
  const culturaMap: Record<string, number> = {};
  const veiculoMap: Record<string, number> = {};
  const origemMap:  Record<string, number> = {};
  const statusMap:  Record<string, number> = {};
  const mesMap:     Record<string, { despesas: number; receitas: number; count: number }> = {};
  const custoMap:   Record<string, number> = {};
  const tipoOpMap:  Record<string, number> = {};
  const agendados:  any[] = [];

  for (const ev of eventos) {
    const valor = parseValor(ev);
    const cat   = ev.categoria || 'outros';

    if (CATEGORIAS_DESPESA.includes(cat)) totalDespesas += valor;
    if (CATEGORIAS_RECEITA.includes(cat)) totalReceitas += valor;

    if (!catMap[cat]) catMap[cat] = { count: 0, total: 0 };
    catMap[cat].count++;
    catMap[cat].total += valor;

    if (ev.area_texto)    areaMap[ev.area_texto]       = (areaMap[ev.area_texto]       || 0) + 1;
    if (ev.cultura_texto) culturaMap[ev.cultura_texto] = (culturaMap[ev.cultura_texto] || 0) + 1;
    if (ev.veiculo_texto) veiculoMap[ev.veiculo_texto] = (veiculoMap[ev.veiculo_texto] || 0) + 1;

    const origem = ev.origem || 'manual';
    origemMap[origem] = (origemMap[origem] || 0) + 1;

    const status = ev.status_kanban || 'registrado';
    statusMap[status] = (statusMap[status] || 0) + 1;

    if (ev.data_evento) {
      const d = new Date(ev.data_evento);
      const mk = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      if (!mesMap[mk]) mesMap[mk] = { despesas: 0, receitas: 0, count: 0 };
      mesMap[mk].count++;
      if (CATEGORIAS_DESPESA.includes(cat)) mesMap[mk].despesas += valor;
      if (CATEGORIAS_RECEITA.includes(cat)) mesMap[mk].receitas += valor;
    }

    if (ev.categoria_custo)
      custoMap[ev.categoria_custo] = (custoMap[ev.categoria_custo] || 0) + valor;

    // Tipo de operação — usa campo estruturado diretamente
    if (cat === 'operacao_agricola') {
      const tipo = ev.tipo_operacao_texto || 'Outros';
      tipoOpMap[tipo] = (tipoOpMap[tipo] || 0) + 1;
    }

    // Todos os eventos vão para o calendário — agendados usam data_programada,
    // demais usam data_evento, fallback para syn_cadmom (data de registro)
    agendados.push(ev);
  }

  return {
    resumo: { totalEventos: eventos.length, totalDespesas, totalReceitas, saldo: totalReceitas - totalDespesas },
    porCategoria:      Object.entries(catMap).map(([k,v]) => ({ categoria: k, ...v })).sort((a,b) => b.count-a.count),
    porArea:           Object.entries(areaMap).map(([k,v]) => ({ area: k, count: v })).sort((a,b) => b.count-a.count),
    porCultura:        Object.entries(culturaMap).map(([k,v]) => ({ cultura: k, count: v })).sort((a,b) => b.count-a.count),
    porVeiculo:        Object.entries(veiculoMap).map(([k,v]) => ({ veiculo: k, count: v })).sort((a,b) => b.count-a.count),
    porOrigem:         Object.entries(origemMap).map(([k,v]) => ({ origem: k, count: v })).sort((a,b) => b.count-a.count),
    porStatus:         Object.entries(statusMap).map(([k,v]) => ({ status: k, count: v })).sort((a,b) => b.count-a.count),
    evolucaoMensal:    Object.entries(mesMap).map(([k,v]) => ({ mes: k, ...v })).sort((a,b) => a.mes.localeCompare(b.mes)),
    porCategoriaCusto: Object.entries(custoMap).map(([k,v]) => ({ categoria_custo: k, total: v })).sort((a,b) => b.total-a.total),
    porTipoOperacao:   Object.entries(tipoOpMap).map(([k,v]) => ({ tipo: k, count: v })).sort((a,b) => b.count-a.count),
    agendados, // todos os eventos (renomeado internamente mas mantemos o campo)
  };
}

// ─── Mini Calendário ──────────────────────────────────────────────────────────
// Exibe TODOS os eventos do período no calendário.
// Data de referência por prioridade: data_programada > data_evento > syn_cadmom.
// Clique num dia seleciona e filtra a lista abaixo — nunca navega para evento.
// Padrão inicial = hoje.

const EMOJI_CAT_CAL: Record<string, string> = {
  operacao_agricola:'🚜', custo:'💸', receita:'💰',
  manutencao:'🔧', abastecimento:'⛽', outros:'📝',
};

/** Retorna a melhor data disponível para posicionar o evento no calendário */
const dataRefEvento = (ev: any): Date | null => {
  const raw = ev.data_programada || ev.data_evento || ev.syn_cadmom;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const MiniCalendario: React.FC<{
  todosEventos: any[];
  onClickEvento: (uid: string) => void;
}> = ({ todosEventos, onClickEvento }) => {
  const hoje = new Date();

  // Dia selecionado: { ano, mes (0-based), dia } — padrão = hoje
  const [diaSel, setDiaSel] = useState<{ ano: number; mes: number; dia: number }>({
    ano: hoje.getFullYear(), mes: hoje.getMonth(), dia: hoje.getDate(),
  });
  const [mesAtual, setMesAtual] = useState(hoje.getMonth());
  const [anoAtual, setAnoAtual] = useState(hoje.getFullYear());

  const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho',
                 'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

  const primeiroDia = new Date(anoAtual, mesAtual, 1).getDay();
  const diasNoMes   = new Date(anoAtual, mesAtual + 1, 0).getDate();

  // Indexa todos os eventos por dia do mês visível
  const porDia: Record<number, any[]> = {};
  for (const ev of todosEventos) {
    const d = dataRefEvento(ev);
    if (!d) continue;
    // Usa getFullYear/getMonth/getDate (local) para compatibilidade com datetime
    const evAno = d.getFullYear();
    const evMes = d.getMonth();
    const evDia = d.getDate();
    if (evAno === anoAtual && evMes === mesAtual) {
      if (!porDia[evDia]) porDia[evDia] = [];
      porDia[evDia].push(ev);
    }
  }

  // Eventos do dia selecionado (pode ser de qualquer mês)
  const eventosDiaSel = todosEventos.filter(ev => {
    const d = dataRefEvento(ev);
    if (!d) return false;
    return d.getFullYear() === diaSel.ano && d.getMonth() === diaSel.mes && d.getDate() === diaSel.dia;
  });

  const prevMes = () => {
    if (mesAtual === 0) { setMesAtual(11); setAnoAtual(a => a-1); }
    else setMesAtual(m => m-1);
  };
  const nextMes = () => {
    if (mesAtual === 11) { setMesAtual(0); setAnoAtual(a => a+1); }
    else setMesAtual(m => m+1);
  };

  const handleClickDia = (dia: number) => {
    setDiaSel({ ano: anoAtual, mes: mesAtual, dia });
  };

  const cells: (number | null)[] = [];
  for (let i = 0; i < primeiroDia; i++) cells.push(null);
  for (let d = 1; d <= diasNoMes; d++) cells.push(d);

  const isHoje = (dia: number) =>
    dia === hoje.getDate() && mesAtual === hoje.getMonth() && anoAtual === hoje.getFullYear();

  const isSelecionado = (dia: number) =>
    dia === diaSel.dia && mesAtual === diaSel.mes && anoAtual === diaSel.ano;

  // Label do dia selecionado para o header da lista
  const diaSelLabel = (() => {
    const d = new Date(diaSel.ano, diaSel.mes, diaSel.dia);
    const isToday = d.toDateString() === hoje.toDateString();
    if (isToday) return 'Hoje';
    return `${String(diaSel.dia).padStart(2,'0')}/${String(diaSel.mes+1).padStart(2,'0')}`;
  })();

  return (
    <div style={{ background:'#fff', borderRadius:14, padding:'20px', boxShadow:'0 2px 12px rgba(0,0,0,0.07)' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <p style={{ margin:0, fontWeight:700, fontSize:'0.82rem', color:'#888', textTransform:'uppercase', letterSpacing:'0.5px' }}>
          📅 Calendário
        </p>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <button onClick={prevMes} style={{ background:'none', border:'1px solid #eee', borderRadius:6, padding:'2px 8px', cursor:'pointer', color:'#555', fontSize:'0.8rem' }}>‹</button>
          <span style={{ fontSize:'0.82rem', fontWeight:600, color:'#444', minWidth:120, textAlign:'center' }}>
            {MESES[mesAtual]} {anoAtual}
          </span>
          <button onClick={nextMes} style={{ background:'none', border:'1px solid #eee', borderRadius:6, padding:'2px 8px', cursor:'pointer', color:'#555', fontSize:'0.8rem' }}>›</button>
        </div>
      </div>

      {/* Cabeçalho dias da semana */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2, marginBottom:4 }}>
        {['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'].map(d => (
          <div key={d} style={{ textAlign:'center', fontSize:'0.68rem', fontWeight:700, color:'#aaa', padding:'2px 0' }}>{d}</div>
        ))}
      </div>

      {/* Grid de dias */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2 }}>
        {cells.map((dia, idx) => {
          if (dia === null) return <div key={`e-${idx}`} />;
          const evs       = porDia[dia] || [];
          const temEvento = evs.length > 0;
          const ehHoje    = isHoje(dia);
          const ehSel     = isSelecionado(dia);

          // Prioridade visual: selecionado > hoje > tem evento > normal
          let bg     = 'transparent';
          let color  = '#555';
          let border = '1px solid transparent';
          let fw     = 400;

          if (ehSel && ehHoje) { bg = '#00b37e'; color = '#fff'; fw = 800; }
          else if (ehSel)      { bg = '#00b37e'; color = '#fff'; fw = 700; }
          else if (ehHoje)     { bg = '#e8f8f2'; color = '#00b37e'; border = '2px solid #00b37e'; fw = 800; }
          else if (temEvento)  { bg = '#f8f8f8'; color = '#444'; border = '1px solid #e8e8e8'; fw = 600; }

          return (
            <div
              key={dia}
              onClick={() => handleClickDia(dia)}
              style={{
                textAlign:'center', padding:'5px 2px', borderRadius:7, fontSize:'0.78rem',
                fontWeight: fw, background: bg, color, border,
                cursor: 'pointer', position:'relative', minHeight:28,
                display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                transition: 'background 0.12s',
              }}
            >
              {dia}
              {/* Bolinha indicadora de eventos */}
              {temEvento && !ehSel && (
                <span style={{
                  display:'block', width:4, height:4, borderRadius:'50%',
                  background: ehHoje ? '#00b37e' : '#aaa', margin:'1px auto 0',
                }} />
              )}
              {/* Badge com contagem quando > 1 */}
              {temEvento && evs.length > 1 && (
                <span style={{
                  position:'absolute', top:1, right:1, fontSize:'0.52rem',
                  background: ehSel ? 'rgba(255,255,255,0.35)' : '#9b59b6',
                  color: ehSel ? '#fff' : '#fff',
                  borderRadius:8, padding:'0 3px', fontWeight:700, lineHeight:'1.4',
                }}>
                  {evs.length}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Lista do dia selecionado — máx 3 linhas visíveis + scroll */}
      <div style={{ marginTop:14, borderTop:'1px solid #f0f0f0', paddingTop:12 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
          <p style={{ margin:0, fontSize:'0.75rem', fontWeight:700, color:'#aaa', textTransform:'uppercase' }}>
            {diaSelLabel}
          </p>
          {eventosDiaSel.length > 0 && (
            <span style={{ fontSize:'0.72rem', color:'#bbb', fontWeight:600 }}>
              {eventosDiaSel.length} evento{eventosDiaSel.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {eventosDiaSel.length === 0 ? (
          <div style={{ textAlign:'center', color:'#ccc', fontSize:'0.82rem', padding:'12px 0' }}>
            Sem eventos neste dia
          </div>
        ) : (
          // maxHeight = 3 linhas × ~36px + margem
          <div style={{ maxHeight:114, overflowY:'auto', paddingRight:2 }}>
            {eventosDiaSel.map(ev => {
              const statusCfg: Record<string,{color:string}> = {
                agendado:     { color:'#9b59b6' },
                registrado:   { color:'#3498db' },
                em_andamento: { color:'#f39c12' },
                concluido:    { color:'#00b37e' },
                cancelado:    { color:'#aaa' },
              };
              const sc = statusCfg[ev.status_kanban] || statusCfg['registrado'];
              return (
                <div
                  key={ev.uid_evento}
                  onClick={() => onClickEvento(ev.uid_evento)}
                  style={{
                    display:'flex', alignItems:'center', gap:8, padding:'6px 8px',
                    borderRadius:8, marginBottom:4, cursor:'pointer',
                    background:'#fafafa', border:'1px solid #f0f0f0',
                    transition:'background 0.12s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f0fff8')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#fafafa')}
                >
                  <span style={{ fontSize:'0.85rem', flexShrink:0 }}>{EMOJI_CAT_CAL[ev.categoria] || '📝'}</span>
                  <span style={{ flex:1, fontSize:'0.82rem', color:'#333', fontWeight:500, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>
                    {ev.titulo}
                  </span>
                  {ev.area_texto && (
                    <span style={{ fontSize:'0.7rem', color:'#bbb', flexShrink:0, overflow:'hidden', whiteSpace:'nowrap', maxWidth:80, textOverflow:'ellipsis' }}>
                      📍{ev.area_texto}
                    </span>
                  )}
                  <span style={{ width:7, height:7, borderRadius:'50%', background:sc.color, flexShrink:0 }} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Subcomponentes ───────────────────────────────────────────────────────────

const KpiCard = ({ label, value, icon, color }: any) => (
  <div style={{
    background:'#fff', borderRadius:14, padding:'16px 20px',
    boxShadow:`0 0 0 1.5px ${color}40, 0 0 16px ${color}25, 0 2px 8px rgba(0,0,0,0.05)`,
    display:'flex', alignItems:'center', gap:14,
  }}>
    <div style={{ fontSize:'1.8rem', lineHeight:1, flexShrink:0 }}>{icon}</div>
    <div style={{ minWidth:0 }}>
      <p className="dash-kpi-label" style={{ margin:0, fontSize:'0.76rem', color:'#888', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', whiteSpace:'nowrap' }}>{label}</p>
      <h2 className="dash-kpi-value" style={{ margin:'2px 0 0', color, fontSize:'1.45rem', fontWeight:800, whiteSpace:'nowrap' }}>{value}</h2>
    </div>
  </div>
);

const ChartCard = ({ title, children, span }: { title: string; children: React.ReactNode; span?: number }) => (
  <div style={{
    background:'#fff', borderRadius:14, padding:'20px',
    boxShadow:'0 2px 12px rgba(0,0,0,0.07)',
    gridColumn: span && span > 1 ? `span ${span}` : undefined,
  }}>
    <p style={{ margin:'0 0 14px', fontWeight:700, fontSize:'0.82rem', color:'#888', textTransform:'uppercase', letterSpacing:'0.5px' }}>
      {title}
    </p>
    {children}
  </div>
);

const TT = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'#fff', border:'1px solid #eee', borderRadius:8, padding:'10px 14px', boxShadow:'0 4px 12px rgba(0,0,0,0.1)' }}>
      <p style={{ margin:'0 0 4px', fontWeight:700, color:'#333', fontSize:'0.85rem' }}>{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ margin:'2px 0', color:p.color, fontSize:'0.82rem' }}>
          {p.name}: {typeof p.value === 'number' && p.value > 50 ? formatBRL(p.value) : p.value}
        </p>
      ))}
    </div>
  );
};

const FilterTag = ({ label, onRemove }: { label: string; onRemove: () => void }) => (
  <span style={{ background:'#e8f8f2', color:'#00b37e', padding:'3px 10px', borderRadius:20, fontSize:'0.75rem', fontWeight:600, display:'flex', alignItems:'center', gap:4 }}>
    {label}
    <button onClick={onRemove} style={{ background:'none', border:'none', cursor:'pointer', color:'#00b37e', padding:0, fontWeight:700 }}>✕</button>
  </span>
);

const Vazio = ({ h = 220 }: { h?: number }) => (
  <div style={{ height:h, display:'flex', alignItems:'center', justifyContent:'center', color:'#ccc', fontSize:'0.85rem' }}>
    Sem dados no período
  </div>
);

// ─── Componente Principal ─────────────────────────────────────────────────────

const AgroDashboard = () => {
  const [periodo, setPeriodo]     = useState('ano');
  const [todosEventos, setTodos]  = useState<any[]>([]);
  const [data, setData]           = useState<DashData | null>(null);
  const [loading, setLoading]     = useState(true);

  const [filtroCategoria,   setFiltroCategoria]   = useState<string | null>(null);
  const [filtroArea,        setFiltroArea]         = useState<string | null>(null);
  const [filtroCultura,     setFiltroCultura]      = useState<string | null>(null);
  const [filtroStatus,      setFiltroStatus]       = useState<string | null>(null);
  const [filtroTipoOp,      setFiltroTipoOp]       = useState<string | null>(null);
  const [filtroVeiculo,     setFiltroVeiculo]      = useState<string | null>(null);
  const [filtroOrigem,      setFiltroOrigem]       = useState<string | null>(null);
  const [filtroCusto,       setFiltroCusto]        = useState<string | null>(null);
  const [filteredEventos,   setFilteredEventos]    = useState<any[]>([]);
  const navigate = useNavigate();

  const getIntervalo = useCallback((p: string) => {
    const hoje = new Date(); const ano = hoje.getFullYear(); const mes = hoje.getMonth();
    const pad = (n: number) => String(n).padStart(2,'0');
    const fmt = (d: Date)   => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    switch (p) {
      case 'mes_atual':   return { inicio: `${ano}-${pad(mes+1)}-01`,   fim: fmt(new Date(ano, mes+1, 0)) };
      case 'mes_passado': return { inicio: `${ano}-${pad(mes)}-01`,     fim: fmt(new Date(ano, mes, 0)) };
      case 'trimestre':   return { inicio: fmt(new Date(ano, mes-2, 1)), fim: fmt(new Date(ano, mes+1, 0)) };
      default:            return { inicio: `${ano}-01-01`,              fim: `${ano}-12-31` };
    }
  }, []);

  const fetchEventos = useCallback(() => {
    setLoading(true);
    const uid = localStorage.getItem('uid_unidade_producao') || '';
    const { inicio, fim } = getIntervalo(periodo);
    const p = new URLSearchParams({ page:'1', limit:'1000', dataInicio:inicio, dataFim:fim, ...(uid && { uid_unidade_producao:uid }) });
    api.get(`/agro/eventos?${p}`)
      .then(res => setTodos(res.data.data || []))
      .catch(() => setTodos([]))
      .finally(() => setLoading(false));
  }, [periodo, getIntervalo]);

  useEffect(() => {
    setFiltroCategoria(null); setFiltroArea(null);
    setFiltroCultura(null);   setFiltroStatus(null); setFiltroTipoOp(null);
    setFiltroVeiculo(null);   setFiltroOrigem(null); setFiltroCusto(null);
    fetchEventos();
  }, [fetchEventos]);

  // Auto-refresh a cada 30 segundos (para monitor na Agrishow)
  useEffect(() => {
    const interval = setInterval(() => {
      const uid = localStorage.getItem('uid_unidade_producao') || '';
      const { inicio, fim } = getIntervalo(periodo);
      const p = new URLSearchParams({ page:'1', limit:'1000', dataInicio:inicio, dataFim:fim, ...(uid && { uid_unidade_producao:uid }) });
      api.get(`/agro/eventos?${p}`)
        .then(res => setTodos(res.data.data || []))
        .catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [periodo, getIntervalo]);

  useEffect(() => {
    const filtrados = todosEventos.filter(ev => {
      if (filtroCategoria && ev.categoria     !== filtroCategoria) return false;
      if (filtroArea      && ev.area_texto    !== filtroArea)      return false;
      if (filtroCultura   && ev.cultura_texto !== filtroCultura)   return false;
      if (filtroStatus    && ev.status_kanban !== filtroStatus)    return false;
      if (filtroTipoOp    && (ev.tipo_operacao_texto || 'Outros')  !== filtroTipoOp) return false;
      if (filtroVeiculo   && ev.veiculo_texto !== filtroVeiculo)   return false;
      if (filtroOrigem    && ev.origem        !== filtroOrigem)    return false;
      if (filtroCusto     && ev.categoria_custo !== filtroCusto)   return false;
      return true;
    });
    setFilteredEventos(filtrados);
    setData(calcularDash(filtrados));
  }, [todosEventos, filtroCategoria, filtroArea, filtroCultura, filtroStatus, filtroTipoOp, filtroVeiculo, filtroOrigem, filtroCusto]);

  const clearFilters = () => {
    setFiltroCategoria(null); setFiltroArea(null);
    setFiltroCultura(null);   setFiltroStatus(null); setFiltroTipoOp(null);
    setFiltroVeiculo(null);   setFiltroOrigem(null); setFiltroCusto(null);
  };
  const hasFilters = filtroCategoria || filtroArea || filtroCultura || filtroStatus || filtroTipoOp || filtroVeiculo || filtroOrigem || filtroCusto;

  return (
    <div className={styles.container}>

      <style>{`
        @media (max-width: 768px) {
          .dash-header       { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; }
          .dash-period-btns  { flex-wrap: wrap !important; gap: 4px !important; }
          .dash-period-btns button { padding: 5px 12px !important; font-size: 0.73rem !important; }
          .dash-kpi-grid     { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
          .dash-chart-grid-4 { grid-template-columns: 1fr !important; gap: 12px !important; }
          .dash-chart-grid-2 { grid-template-columns: 1fr !important; gap: 12px !important; }
          .dash-cal-grid     { grid-template-columns: 1fr !important; gap: 12px !important; }
          .dash-kpi-value    { font-size: 1.2rem !important; }
          .dash-kpi-label    { font-size: 0.68rem !important; }
          .dash-table-col-hide { display: none !important; }
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="dash-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
        <div>
          <h1 className={styles.greenTitle} style={{ marginBottom:6 }}>Dashboard</h1>
          {hasFilters && (
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
              <span style={{ fontSize:'0.78rem', color:'#888' }}>Filtros:</span>
              {filtroCategoria && <FilterTag label={`Categoria: ${LABEL_CAT[filtroCategoria]||filtroCategoria}`} onRemove={() => setFiltroCategoria(null)} />}
              {filtroArea      && <FilterTag label={`Área: ${filtroArea}`}                                       onRemove={() => setFiltroArea(null)} />}
              {filtroCultura   && <FilterTag label={`Cultura: ${filtroCultura}`}                                 onRemove={() => setFiltroCultura(null)} />}
              {filtroStatus    && <FilterTag label={`Status: ${STATUS_LABELS[filtroStatus]||filtroStatus}`}      onRemove={() => setFiltroStatus(null)} />}
              {filtroTipoOp    && <FilterTag label={`Tipo Op.: ${filtroTipoOp}`}                                 onRemove={() => setFiltroTipoOp(null)} />}
              {filtroVeiculo   && <FilterTag label={`Veículo: ${filtroVeiculo}`}                                 onRemove={() => setFiltroVeiculo(null)} />}
              {filtroOrigem    && <FilterTag label={`Origem: ${filtroOrigem}`}                                   onRemove={() => setFiltroOrigem(null)} />}
              {filtroCusto     && <FilterTag label={`Cat. Custo: ${filtroCusto}`}                                onRemove={() => setFiltroCusto(null)} />}
              <button onClick={clearFilters} style={{ fontSize:'0.75rem', color:'#e74c3c', background:'none', border:'none', cursor:'pointer', fontWeight:700 }}>✕ Limpar</button>
            </div>
          )}
        </div>
        <div className="dash-period-btns" style={{ display:'flex', gap:8 }}>
          {(['mes_atual','mes_passado','trimestre','ano'] as const).map((k,i) => {
            const labels = ['Este mês','Mês passado','Trimestre','Este ano'];
            return (
              <button key={k} onClick={() => setPeriodo(k)} style={{
                padding:'6px 14px', borderRadius:20, border:'none', cursor:'pointer', fontWeight:600, fontSize:'0.8rem',
                background: periodo === k ? '#00b37e' : '#f0f0f0',
                color:      periodo === k ? '#fff'    : '#555',
              }}>{labels[i]}</button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:80, color:'#bbb' }}>🌱 Carregando...</div>
      ) : !data ? null : (
        <>
          {/* ── KPIs ──────────────────────────────────────────────────────── */}
          <div className="dash-kpi-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 }}>
            <KpiCard label="Total de Eventos" value={data.resumo.totalEventos}              icon="📋" color="#3498db" />
            <KpiCard label="Total Gasto"       value={formatBRL(data.resumo.totalDespesas)} icon="💸" color="#e74c3c" />
            <KpiCard label="Total Recebido"    value={formatBRL(data.resumo.totalReceitas)} icon="💰" color="#00b37e" />
            <KpiCard label="Saldo"             value={formatBRL(data.resumo.saldo)}
              icon={data.resumo.saldo >= 0 ? '📈' : '📉'}
              color={data.resumo.saldo >= 0 ? '#00b37e' : '#e74c3c'}
            />
          </div>

          {/* ── Linha 1: Categoria + Veículo + Status (pizza) + Origem ─── */}
          <div className="dash-chart-grid-4" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:18, marginBottom:18 }}>

            <ChartCard title="🏷️ Por Categoria — clique para filtrar">
              {data.porCategoria.length === 0 ? <Vazio /> : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.porCategoria.map(c => ({ ...c, nome: LABEL_CAT[c.categoria]||c.categoria }))} layout="vertical" margin={{ left:8, right:8 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f5f5f5" />
                    <XAxis type="number" tick={{ fontSize:10 }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="nome" tick={{ fontSize:10 }} width={95} axisLine={false} tickLine={false} />
                    <Tooltip content={<TT />} />
                    <Bar dataKey="count" name="Eventos" radius={[0,4,4,0]} cursor="pointer"
                      onClick={(d:any) => { const c=d?.categoria; if(c) setFiltroCategoria(p=>p===c?null:c); }}
                    >
                      {data.porCategoria.map((e,i) => (
                        <Cell key={i} fill={filtroCategoria===e.categoria?'#00b37e':PALETTE[i%PALETTE.length]}
                          opacity={filtroCategoria&&filtroCategoria!==e.categoria?0.35:1} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="🚜 Por Veículo — clique para filtrar">
              {data.porVeiculo.length === 0
                ? <div style={{ height:220, display:'flex', alignItems:'center', justifyContent:'center', color:'#ccc', fontSize:'0.85rem' }}>Sem eventos com veículo</div>
                : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={data.porVeiculo.slice(0,6)} dataKey="count" nameKey="veiculo"
                      cx="50%" cy="50%" innerRadius={48} outerRadius={78} paddingAngle={3}
                      cursor="pointer"
                      onClick={(d:any) => { const v=d?.veiculo; if(v) setFiltroVeiculo((p:string|null)=>p===v?null:v); }}
                    >
                      {data.porVeiculo.slice(0,6).map((e,i) => (
                        <Cell key={i} fill={PALETTE_WARM[i%PALETTE_WARM.length]}
                          opacity={filtroVeiculo && filtroVeiculo !== e.veiculo ? 0.3 : 1} />
                      ))}
                    </Pie>
                    <Legend iconSize={10} />
                    <Tooltip formatter={(v:any,name:any) => [`${v} eventos`, name]} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="🔖 Por Status — clique para filtrar">
              {data.porStatus.length === 0 ? <Vazio /> : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.porStatus.map(s => ({ ...s, nome: STATUS_LABELS[s.status]||s.status }))}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f5f5f5" />
                    <XAxis dataKey="nome" tick={{ fontSize:10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize:11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" name="Eventos" radius={[4,4,0,0]} cursor="pointer"
                      onClick={(d:any) => { const st=d?.status; if(st) setFiltroStatus((p:string|null)=>p===st?null:st); }}
                    >
                      {data.porStatus.map((e,i) => (
                        <Cell key={i} fill={STATUS_COLORS[e.status]||PALETTE[i]} opacity={filtroStatus&&filtroStatus!==e.status?0.3:1} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="📱 Por Origem — clique para filtrar">
              {data.porOrigem.length === 0 ? <Vazio /> : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={data.porOrigem} dataKey="count" nameKey="origem" cx="50%" cy="50%"
                      innerRadius={45} outerRadius={82} paddingAngle={3}
                      cursor="pointer"
                      onClick={(d:any) => { const o=d?.origem; if(o) setFiltroOrigem((p:string|null)=>p===o?null:o); }}
                    >
                      {data.porOrigem.map((e,i) => (
                        <Cell key={i} fill={PALETTE[i%PALETTE.length]}
                          opacity={filtroOrigem && filtroOrigem !== e.origem ? 0.3 : 1} />
                      ))}
                    </Pie>
                    <Legend iconSize={10} />
                    <Tooltip formatter={(v:any,name:any) => [`${v} eventos`, name]} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* ── Linha 2: Tipo de Operação + Calendário ──────────────────────── */}
          <div className="dash-cal-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:18, marginBottom:18 }}>

            <ChartCard title="🚜 Tipo de Operação Agrícola — clique para filtrar">
              {data.porTipoOperacao.length === 0
                ? <Vazio />
                : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.porTipoOperacao} layout="vertical" margin={{ left:8, right:40 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f5f5f5" />
                    <XAxis type="number" tick={{ fontSize:10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="tipo" tick={{ fontSize:11 }} width={120} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(v: any) => [`${v} evento${v !== 1 ? 's' : ''}`, 'Qtd']} />
                    <Bar dataKey="count" name="Eventos" radius={[0,6,6,0]} cursor="pointer"
                      onClick={(d:any) => { const t=d?.tipo; if(t) setFiltroTipoOp(p=>p===t?null:t); }}
                      label={{ position:'right', fontSize:12, fontWeight:700, fill:'#555', formatter:(v: any) => v }}
                    >
                      {data.porTipoOperacao.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={CORES_TIPO_OP[entry.tipo] || PALETTE[i % PALETTE.length]}
                          opacity={filtroTipoOp && filtroTipoOp !== entry.tipo ? 0.3 : 1}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            {/* Mini-calendário — todos os eventos do período */}
            <MiniCalendario todosEventos={filteredEventos} onClickEvento={uid => navigate(`/agro/eventos/${uid}`)} />
          </div>

          {/* ── Linha 3: Cultura (colunas, full width) ──────────────────────── */}
          <div style={{ marginBottom:18 }}>
            <ChartCard title="🌱 Por Cultura — clique para filtrar">
              {data.porCultura.length === 0
                ? <div style={{ height:200, display:'flex', alignItems:'center', justifyContent:'center', color:'#ccc', fontSize:'0.85rem' }}>Sem eventos com cultura</div>
                : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data.porCultura.slice(0,15)} margin={{ top:10, right:20, left:0, bottom:60 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f5f5f5" />
                    <XAxis dataKey="cultura" tick={{ fontSize:11, fill:'#555' }} axisLine={false} tickLine={false}
                      interval={0} angle={-35} textAnchor="end" />
                    <YAxis tick={{ fontSize:11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip formatter={(v:any) => [`${v} eventos`, 'Qtd']} />
                    <Bar dataKey="count" name="Eventos" radius={[6,6,0,0]} cursor="pointer"
                      onClick={(d:any) => { const c=d?.cultura; if(c) setFiltroCultura((p:string|null)=>p===c?null:c); }}
                      label={{ position:'top', fontSize:11, fontWeight:700, fill:'#555', formatter:(v:any)=>v }}
                    >
                      {data.porCultura.slice(0,15).map((e,i) => (
                        <Cell key={i} fill={PALETTE_COOL[i%PALETTE_COOL.length]} opacity={filtroCultura&&filtroCultura!==e.cultura?0.3:1} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* ── Linha 4: Área (3fr) + Despesas por Custo (1fr) ─────────────── */}
          <div style={{ display:'grid', gridTemplateColumns:'3fr 1fr', gap:18, marginBottom:18 }}>

            <ChartCard title="📍 Eventos por Área — clique para filtrar">
              {data.porArea.length === 0
                ? <div style={{ height:220, display:'flex', alignItems:'center', justifyContent:'center', color:'#ccc', fontSize:'0.85rem' }}>Sem eventos com área</div>
                : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.porArea.slice(0,10)}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f5f5f5" />
                    <XAxis dataKey="area" tick={{ fontSize:10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize:11 }} axisLine={false} tickLine={false} />
                    <Tooltip />
                    <Bar dataKey="count" name="Eventos" radius={[4,4,0,0]} cursor="pointer"
                      onClick={(d:any) => { const a=d?.area; if(a) setFiltroArea(p=>p===a?null:a); }}
                    >
                      {data.porArea.slice(0,10).map((e,i) => (
                        <Cell key={i} fill={PALETTE[i%PALETTE.length]} opacity={filtroArea&&filtroArea!==e.area?0.3:1} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            {data.porCategoriaCusto.length > 0 ? (
              <ChartCard title="💰 Despesas por Categoria de Custo — clique para filtrar">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.porCategoriaCusto.slice(0,8)} layout="vertical" margin={{ left:4, right:16 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f5f5f5" />
                    <XAxis type="number" tick={{ fontSize:10 }} axisLine={false} tickLine={false} tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} />
                    <YAxis type="category" dataKey="categoria_custo" tick={{ fontSize:10 }} width={80} axisLine={false} tickLine={false} />
                    <Tooltip content={<TT />} />
                    <Bar dataKey="total" name="Total" radius={[0,4,4,0]} cursor="pointer"
                      onClick={(d:any) => { const c=d?.categoria_custo; if(c) setFiltroCusto((p:string|null)=>p===c?null:c); }}
                    >
                      {data.porCategoriaCusto.slice(0,8).map((e,i) => (
                        <Cell key={i} fill={PALETTE_WARM[i%PALETTE_WARM.length]}
                          opacity={filtroCusto && filtroCusto !== e.categoria_custo ? 0.3 : 1} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            ) : <div />}
          </div>

          {/* ── Tabela de Registros Filtrados ────────────────────────────── */}
          <div style={{ marginTop:18 }}>
            <div style={{
              background:'#fff', borderRadius:14,
              boxShadow:'0 2px 12px rgba(0,0,0,0.07)', overflow:'hidden',
            }}>
              <div style={{
                background:'#00b37e', padding:'14px 20px',
                display:'flex', alignItems:'center', justifyContent:'space-between',
              }}>
                <span style={{ color:'#fff', fontWeight:700, fontSize:'0.85rem', textTransform:'uppercase', letterSpacing:'0.5px' }}>
                  📋 Registros {hasFilters ? 'filtrados' : 'do período'}
                </span>
                <span style={{ color:'rgba(255,255,255,0.85)', fontSize:'0.8rem', fontWeight:600 }}>
                  {filteredEventos.length} {filteredEventos.length === 1 ? 'evento' : 'eventos'}
                </span>
              </div>

              <div style={{ maxHeight:380, overflowY:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ position:'sticky', top:0, background:'#f8f9fa', zIndex:1 }}>
                      <th style={{ padding:'10px 20px', textAlign:'left',   fontSize:'0.78rem', fontWeight:700, color:'#888', textTransform:'uppercase', borderBottom:'1px solid #f0f0f0', width:'38%' }}>Título</th>
                      <th className="dash-table-col-hide" style={{ padding:'10px 12px', textAlign:'left', fontSize:'0.78rem', fontWeight:700, color:'#888', textTransform:'uppercase', borderBottom:'1px solid #f0f0f0' }}>Categoria</th>
                      <th className="dash-table-col-hide" style={{ padding:'10px 12px', textAlign:'left', fontSize:'0.78rem', fontWeight:700, color:'#888', textTransform:'uppercase', borderBottom:'1px solid #f0f0f0' }}>Tipo Op.</th>
                      <th style={{ padding:'10px 12px', textAlign:'right',  fontSize:'0.78rem', fontWeight:700, color:'#888', textTransform:'uppercase', borderBottom:'1px solid #f0f0f0' }}>Valor</th>
                      <th style={{ padding:'10px 12px', textAlign:'center', fontSize:'0.78rem', fontWeight:700, color:'#888', textTransform:'uppercase', borderBottom:'1px solid #f0f0f0' }}>Data</th>
                      <th style={{ padding:'10px 12px', textAlign:'center', fontSize:'0.78rem', fontWeight:700, color:'#888', textTransform:'uppercase', borderBottom:'1px solid #f0f0f0' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEventos.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ padding:'32px 20px', textAlign:'center', color:'#bbb', fontSize:'0.9rem' }}>
                          Nenhum evento encontrado para os filtros aplicados.
                        </td>
                      </tr>
                    )}
                    {filteredEventos.map((ev, idx) => {
                      const valor = parseFloat(ev.valor_total || ev.valor || '0') || 0;
                      const d = ev.data_evento ? new Date(ev.data_evento) : null;
                      const dataFmt = d
                        ? `${String(d.getUTCDate()).padStart(2,'0')}/${String(d.getUTCMonth()+1).padStart(2,'0')}`
                        : '—';
                      const statusCfg: Record<string,{label:string;color:string;bg:string}> = {
                        agendado:     { label:'Agendado',     color:'#9b59b6', bg:'#f5f0ff' },
                        registrado:   { label:'Registrado',   color:'#3498db', bg:'#ebf5fb' },
                        em_andamento: { label:'Em andamento', color:'#f39c12', bg:'#fef9e7' },
                        concluido:    { label:'Concluído',    color:'#00b37e', bg:'#f0fff8' },
                        cancelado:    { label:'Cancelado',    color:'#999',    bg:'#f5f5f5' },
                      };
                      const sc = statusCfg[ev.status_kanban] || statusCfg['registrado'];
                      const EMOJI: Record<string,string> = { operacao_agricola:'🚜', custo:'💸', receita:'💰', manutencao:'🔧', abastecimento:'⛽', outros:'📝' };
                      const LCAT: Record<string,string>  = { operacao_agricola:'Op. Agrícola', custo:'Custo', receita:'Receita', manutencao:'Manutenção', abastecimento:'Abastecimento', outros:'Outros' };
                      return (
                        <tr
                          key={ev.uid_evento || idx}
                          onClick={() => navigate(`/agro/eventos/${ev.uid_evento}`)}
                          style={{ cursor:'pointer', borderBottom:'1px solid #f8f8f8', transition:'background 0.12s' }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#f8fffe')}
                          onMouseLeave={e => (e.currentTarget.style.background = '')}
                        >
                          <td style={{ padding:'11px 20px', fontSize:'0.87rem', fontWeight:500, color:'#333' }}>
                            <span style={{ marginRight:6 }}>{EMOJI[ev.categoria] || '📝'}</span>
                            {ev.titulo}
                          </td>
                          <td className="dash-table-col-hide" style={{ padding:'11px 12px', fontSize:'0.83rem', color:'#666' }}>
                            {LCAT[ev.categoria] || ev.categoria}
                          </td>
                          <td className="dash-table-col-hide" style={{ padding:'11px 12px', fontSize:'0.83rem', color:'#888' }}>
                            {ev.categoria === 'operacao_agricola' && ev.tipo_operacao_texto
                              ? <span style={{ background:'#f0f8ff', color:'#3498db', padding:'2px 8px', borderRadius:10, fontSize:'0.75rem', fontWeight:600 }}>{ev.tipo_operacao_texto}</span>
                              : '—'}
                          </td>
                          <td style={{ padding:'11px 12px', textAlign:'right', fontWeight:600, fontSize:'0.87rem', color: ev.categoria === 'receita' ? '#00b37e' : '#333' }}>
                            {valor > 0 ? formatBRL(valor) : '—'}
                          </td>
                          <td style={{ padding:'11px 12px', textAlign:'center', fontSize:'0.83rem', color:'#888' }}>
                            {dataFmt}
                          </td>
                          <td style={{ padding:'11px 12px', textAlign:'center' }}>
                            <span style={{ background:sc.bg, color:sc.color, padding:'3px 10px', borderRadius:20, fontSize:'0.73rem', fontWeight:600, whiteSpace:'nowrap' }}>
                              {sc.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    <div style={{ height: 32 }} />
    </div>
  );
};

export default AgroDashboard;